import { RootState } from '@algoan/dashboard/store/store';
import {
  WidgetConfig,
  ApplicationTableConfig,
  TeamConfig,
  Link as LinkConfig,
  defaultScoreConfig,
  ScoreRange,
  ScoreConfig,
  ShareConfig,
  AsyncStatus,
  maximumScoreValue,
  minimumScoreValue,
  WindowConfiguration,
} from '@algoan/dashboard/model';
import { findScoreRange, isShareConfigValid } from '@algoan/dashboard/calculators';
import { theme } from '@algoan/dashboard/theme';
import { createSelector } from '@reduxjs/toolkit';
import { CountryCode } from '@algoan/deep-dive/countries';

export const appConfigLoadingSelector: (state: RootState) => boolean = createSelector(
  (state: RootState): AsyncStatus => state.appConfig.configFetchingStatus,
  (configFetchingStatus: AsyncStatus): boolean => configFetchingStatus === AsyncStatus.PENDING,
);

export const appConfigLoadedSelector: (state: RootState) => boolean = createSelector(
  (state: RootState): AsyncStatus => state.appConfig.configFetchingStatus,
  (configFetchingStatus: AsyncStatus): boolean => configFetchingStatus === AsyncStatus.RESOLVED,
);

export const widgetConfigSelector = (state: RootState): WidgetConfig | undefined =>
  state.appConfig?.config?.dashboard.widgets;

export const minimumNumberOfTransactionsToDisplayFrequentTransactionsSelector = (state: RootState): number =>
  state.appConfig?.config?.dashboard.minimumNumberOfTransactionsToDisplayFrequentTransactions ?? 5;

export const applicationTableConfigSelector = (state: RootState): ApplicationTableConfig | undefined =>
  state.appConfig?.config?.dashboard.applicationTable;

export const teamConfigSelector = (state: RootState): TeamConfig | undefined =>
  state.appConfig?.config?.dashboard.teamConfig;

export const linkCreationEnabledSelector = (state: RootState): boolean => !!state.appConfig.config?.dashboard.link?.url;

export const linkConfigSelector = (state: RootState): LinkConfig | undefined => state.appConfig.config?.dashboard.link;

export const countryCodeSelector = (state: RootState): CountryCode | undefined =>
  state.appConfig.config?.dashboard.countryCode;

export const filterColumnsSelector = (state: RootState): string[] =>
  state.appConfig.config?.dashboard.applicationTable?.filterByColumns ?? ['customIdentifier'];

export const scoreRangeConfigSelector =
  (scoreValue: number | undefined) =>
  (state: RootState): ScoreRange => {
    const defaultRange = {
      min: minimumScoreValue,
      max: maximumScoreValue,
      startColor: theme.secondary.chalk,
      endColor: theme.secondary.chalk,
    };

    if (typeof scoreValue !== 'number') {
      return defaultRange;
    }

    const scoreConfig = state.appConfig.config?.dashboard.scoreConfig || defaultScoreConfig;
    return findScoreRange(scoreValue, scoreConfig) ?? defaultRange;
  };

export const scoreConfigSelector = (state: RootState): ScoreConfig | undefined =>
  state.appConfig.config?.dashboard.scoreConfig;

export const isRecategorizationEnabledSelector = (state: RootState): boolean =>
  state.appConfig.config?.dashboard.isRecategorizationEnabled ?? true;

export const sharingConfigSelector: (state: RootState) => ShareConfig | undefined = createSelector(
  (state: RootState): ShareConfig | undefined => state.appConfig.config?.dashboard.shareConfig,
  (shareConfig): ShareConfig | undefined => (isShareConfigValid(shareConfig) ? shareConfig : undefined),
);

export const analysisWindowConfigSelector = (state: RootState): WindowConfiguration | undefined =>
  state.appConfig?.config?.creditInsights?.computing?.window;

export const smsTemplateConfigSelector = (state: RootState): Record<string, string> | undefined =>
  state.appConfig?.config?.transactionDataConfig?.smsTemplatePerLanguage;

export const isRefreshEnabledSelector = (state: RootState): boolean =>
  !!state.appConfig?.config?.projectConfig?.configuration?.aggregation?.enableRefresh;

export const currencySelector = (state: RootState): string | undefined =>
  state.appConfig?.config?.projectConfig?.configuration?.computing?.currency;
